import { useState } from "react";
import {
  Typography,
  Dialog,
  DialogHeader,
  DialogBody,
  Chip,
} from "@material-tailwind/react";

export default function RelistBreakdownDialog({ open, onClose, relistDom, reg, title, currentListingId }) {
  const [footnoteOpen, setFootnoteOpen] = useState(false);
  if (!relistDom) return null;

  return (
    <Dialog open={open} handler={onClose} size="lg">
      <DialogHeader className="justify-between">
        <div>
          <Typography variant="h5" color="blue-gray">
            Days on Market Breakdown
          </Typography>
          {(reg || title) && (
            <Typography variant="small" color="gray" className="font-normal">
              {reg}{title ? ` — ${title}` : ''}
            </Typography>
          )}
        </div>
        {relistDom.relistCount > 1 && (
          <Chip color="amber" value={`${relistDom.relistCount} Listings`} variant="ghost" />
        )}
      </DialogHeader>
      <DialogBody divider className="overflow-y-auto max-h-[70vh]">
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <Typography variant="h3" color="blue">{relistDom.trueDom}</Typography>
              <Typography variant="small" color="gray">True DOM (days)</Typography>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Typography variant="h3" color="blue-gray">
                {(relistDom.cycleViews || relistDom.listings?.[0]?.details_views || 0).toLocaleString()}
              </Typography>
              <Typography variant="small" color="gray">Total Views</Typography>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Typography variant="h3" color="blue-gray">
                {(relistDom.cycleImpressions || relistDom.listings?.[0]?.impressions || 0).toLocaleString()}
              </Typography>
              <Typography variant="small" color="gray">Total Impressions</Typography>
            </div>
          </div>

          {relistDom.listings && relistDom.listings.length > 0 && (
            <div>
              <Typography variant="h6" color="blue-gray" className="mb-3">
                {relistDom.relistCount > 1 ? "Listing History" : "Listing Details"}
              </Typography>
              <table className="w-full table-auto text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    {["#", "Listing ID", "Status", "Listed", "Last Seen", "Ctrl DOM", "Adjusted DOM", "Views", "Impressions", "Weeks"].map((h) => (
                      <th key={h} className="py-2 px-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {relistDom.listings.map((l, i) => {
                    const isCurrent = currentListingId != null && l.listing_id === currentListingId;
                    return (
                      <tr key={l.listing_id} className={`border-b border-gray-100 ${isCurrent ? "bg-blue-50" : ""}`}>
                        <td className="py-2 px-3 font-medium">{i + 1}</td>
                        <td className="py-2 px-3 font-mono text-xs">
                          {l.listing_id}
                          {isCurrent && <Chip size="sm" value="current" color="blue" variant="ghost" className="ml-2 inline-block" />}
                        </td>
                        <td className="py-2 px-3">
                          <Chip size="sm" value={l.status || "—"} color={l.status === "ACTIVE" ? "green" : l.status === "DELETED" ? "gray" : "blue"} variant="ghost" />
                        </td>
                        <td className="py-2 px-3 text-xs">
                          {l.ws_creation_date ? new Date(l.ws_creation_date?.toDate ? l.ws_creation_date.toDate() : l.ws_creation_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                        </td>
                        <td className="py-2 px-3 text-xs">
                          {l.scraper_last_seen ? new Date(l.scraper_last_seen?.toDate ? l.scraper_last_seen.toDate() : l.scraper_last_seen).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                        </td>
                        <td className="py-2 px-3 text-gray-400">{l.days_aging || "—"}</td>
                        <td className="py-2 px-3 font-medium">
                          {(() => {
                            const start = l.ws_creation_date?.toDate ? l.ws_creation_date.toDate() : l.ws_creation_date ? new Date(l.ws_creation_date) : null;
                            const end = l.scraper_last_seen?.toDate ? l.scraper_last_seen.toDate() : l.scraper_last_seen ? new Date(l.scraper_last_seen) : null;
                            return start && end ? Math.round((end - start) / 86400000) : "—";
                          })()}
                        </td>
                        <td className="py-2 px-3">{(l.details_views || 0).toLocaleString()}</td>
                        <td className="py-2 px-3">{(l.impressions || 0).toLocaleString()}</td>
                        <td className="py-2 px-3">{l.weeks_tracked || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {relistDom.relistCount > 1 && (
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 font-semibold">
                      <td className="py-2 px-3" colSpan={5}>Aggregated Total</td>
                      <td className="py-2 px-3 text-gray-400">{relistDom.listings.reduce((s, l) => s + (l.days_aging || 0), 0)}</td>
                      <td className="py-2 px-3 text-blue-600">{relistDom.trueDom}* (True DOM)</td>
                      <td className="py-2 px-3 text-blue-600">{(relistDom.cycleViews || 0).toLocaleString()}</td>
                      <td className="py-2 px-3 text-blue-600">{(relistDom.cycleImpressions || 0).toLocaleString()}</td>
                      <td className="py-2 px-3 text-blue-600">{relistDom.listings.reduce((s, l) => s + (l.weeks_tracked || 0), 0)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          <div className="pt-2 border-t border-gray-100">
            <p
              className="text-sm text-blue-600 cursor-pointer hover:text-blue-800"
              onClick={() => setFootnoteOpen(!footnoteOpen)}
            >
              * How is True DOM calculated? {footnoteOpen ? "▲" : "▼"}
            </p>
            {footnoteOpen && (
              <div className="mt-2 text-sm text-gray-800">
                <p>Controller.com resets Days on Market to zero each time an aircraft is relisted with a new listing ID, and may continue counting after a listing is removed from the site. <strong>Ctrl DOM</strong> shows this raw value for reference. <strong>Adjusted DOM</strong> corrects for both issues by using the Controller CSV to determine when a listing was first created (Listed) and the PlanePortal scraper to determine when it was last seen on the site (Last Seen, updated 3x/week). <strong>True DOM</strong> is the full span from the earliest listing date to the most recent sighting across all listing IDs in the same marketing cycle. A new cycle starts when a confirmed sale is detected or the aircraft was off-market for more than 7 days.</p>
              </div>
            )}
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}
